"use client"

import React, { useEffect, useMemo, useRef, useState } from "react"
import { Loader2, Search } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Blade, PillButton } from "@/components/blades/blade"
import { useBlades } from "@/components/blades/blade-stack"
import { useExplorerContext } from "@/components/explorer/explorer-context"
import { EntityMapCanvas } from "@/components/explorer/entity-map/entity-map-canvas"
import { useEntityGraph } from "@/hooks/use-entity-graph"
import { groupColor, litPath, neighboursOf, type EntityGraph, type GraphEdge, type GraphNode } from "@/lib/entity-graph"
import { layoutEntityGraph, type Layout } from "@/lib/entity-layout"
import { useTranslation, type Translator } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { RecordBladeParams } from "@/components/explorer/record-blade"

export interface EntityMapBladeProps {
  open: boolean
  onClose: () => void
  depth?: number
  trail?: string[]
  onTrailClick?: (index: number) => void
}

/**
 * The tenant's entity graph as a pan-and-zoom map. Clicking a node selects that entity
 * in the explorer (records reload behind the blade); the side pane lists what the
 * selection connects to and through which fields. The open blade path is drawn lit.
 */
export function EntityMapBlade({ open, onClose, depth, trail, onTrailClick }: EntityMapBladeProps) {
  const { atsSystem, entityTypes, selectedEntity, selectEntity } = useExplorerContext()
  const blades = useBlades()
  const { t, n } = useTranslation()
  const { graph, resolving, resolve } = useEntityGraph(atsSystem, entityTypes, selectedEntity?.name ?? null)
  const [focus, setFocus] = useState<string | null>(selectedEntity?.name ?? null)
  const [isolate, setIsolate] = useState(true)
  const [query, setQuery] = useState("")
  const [hover, setHover] = useState<{ edge: GraphEdge | null; node: GraphNode | null }>({ edge: null, node: null })

  useEffect(() => {
    if (selectedEntity) setFocus(selectedEntity.name)
  }, [selectedEntity])

  const { layout, layingOut } = useDebouncedLayout(graph)
  const openEntities = blades.stack.filter((e) => e.kind === "record").map((e) => (e.params as RecordBladeParams).entityType)
  const lit = useMemo(() => litPath(selectedEntity?.name ?? null, openEntities), [selectedEntity, openEntities])

  const focusNode = focus ? graph.nodes.find((n) => n.id === focus) ?? null : null
  const neighbours = useMemo(() => (focus ? neighboursOf(graph, focus) : []), [graph, focus])
  const matches = useMemo(() => {
    const term = query.trim().toLowerCase()
    return term ? graph.nodes.filter((n) => n.label.toLowerCase().includes(term) || n.id.toLowerCase().includes(term)).slice(0, 8) : []
  }, [graph.nodes, query])

  const pick = (id: string) => {
    setFocus(id)
    resolve(id)
    const entity = entityTypes.find((e) => e.name === id)
    if (entity && entity.name !== selectedEntity?.name) selectEntity(entity)
  }

  const groupsInView = useMemo(() => Array.from(new Set(graph.nodes.map((n) => n.group))), [graph.nodes])

  return (
    <Blade
      open={open}
      onClose={onClose}
      size="xl"
      depth={depth}
      trail={trail}
      onTrailClick={onTrailClick}
      eyebrow={atsSystem ?? undefined}
      title={t("explorer.entityMap.title")}
      description={`${t("explorer.entityMap.summary", { entities: n(graph.nodes.length), associations: n(graph.edges.length) })}${
        resolving.size > 0 ? ` · ${t("explorer.entityMap.resolving", { count: n(resolving.size) })}` : ""
      }`}
      bare
      footer={
        <>
          <span className="text-xs text-muted-foreground">{t("explorer.entityMap.footerHint")}</span>
          <PillButton onClick={onClose} disabled={!selectedEntity}>
            {t("explorer.entityMap.browse", { entity: selectedEntity?.displayName ?? t("explorer.entityMap.records") })}
          </PillButton>
        </>
      }
    >
      <div className="flex h-full min-h-[520px]">
        <div className="relative min-w-0 flex-1" style={{ background: "radial-gradient(ellipse at 50% 40%, #16295c 0%, #0f214b 70%)" }}>
          {layout.size > 0 ? (
            <EntityMapCanvas graph={graph} layout={layout} selected={focus} lit={lit} isolate={isolate} onSelect={pick} onHover={(edge, node) => setHover({ edge, node })} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-[#c7d2f0]">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">{t("explorer.entityMap.loading")}</span>
            </div>
          )}
          <MapProgress layingOut={layingOut} resolving={resolving.size} entities={graph.nodes.length} />
          {(hover.edge || hover.node) && (
            <div className="pointer-events-none absolute left-4 top-4 rounded-lg bg-white/95 px-3 py-1.5 text-xs text-[#131313] shadow-menu">
              {hover.node ? (
                <>
                  <span className="font-semibold">{hover.node.label}</span> · {hover.node.group}
                  {` · ${fieldCountLabel(hover.node.fieldCount, t, n)}`}
                </>
              ) : hover.edge ? (
                <>
                  <span className="font-semibold">{hover.edge.from}</span>.{hover.edge.field} → <span className="font-semibold">{hover.edge.to}</span>
                  <span className="ml-1 text-muted-foreground">({t((hover.edge.kind === "to-many" ? "explorer.entityMap.kinds.toMany" : "explorer.entityMap.kinds.toOne"))})</span>
                </>
              ) : null}
            </div>
          )}
          <div className="absolute bottom-3 left-4 flex flex-wrap gap-1.5">
            {groupsInView.map((group) => (
              <span key={group} className="inline-flex items-center gap-1 rounded-pill bg-[#0b1735]/80 px-2 py-0.5 text-[10px] text-[#e7ebf5]">
                <span className="h-2 w-2 rounded-full" style={{ background: groupColor(group) }} /> {group}
              </span>
            ))}
          </div>
        </div>

        <aside className="flex w-[300px] shrink-0 flex-col border-l border-card-border">
          <div className="border-b border-card-border p-3">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input className="h-8 pl-7 text-xs" placeholder={t("explorer.entityMap.findEntity")} value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            {matches.length > 0 && (
              <ul className="mt-1 max-h-40 overflow-auto rounded-md border border-card-border bg-card text-xs shadow-menu">
                {matches.map((n) => (
                  <li key={n.id}>
                    <button type="button" className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-row-hover" onClick={() => { pick(n.id); setQuery("") }}>
                      <span className="h-2 w-2 rounded-full" style={{ background: groupColor(n.group) }} />
                      {n.label}
                      <span className="ml-auto text-[10px] text-muted-foreground">{n.group}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={isolate} onCheckedChange={setIsolate} className="scale-90" /> {t("explorer.entityMap.isolate")}
            </label>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {focusNode ? (
              <>
                <div className="mb-3">
                  <div className="th-section-label">{t("explorer.entityMap.selected")}</div>
                  <div className="font-display text-base font-bold">{focusNode.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {focusNode.group}
                    {focusNode.fieldCount !== null ? ` · ${fieldCountLabel(focusNode.fieldCount, t, n)}` : ""}
                    {resolving.has(focusNode.id)
                      ? ` · ${t("explorer.entityMap.loadingAssociations")}`
                      : !focusNode.resolved
                        ? ` · ${t("explorer.entityMap.associationsNotLoaded")}`
                        : ""}
                  </div>
                </div>
                <div className="th-section-label mb-1">
                  {t("explorer.entityMap.connectsTo")} · {n(neighbours.length)}
                </div>
                {neighbours.length === 0 && <p className="text-xs text-muted-foreground">{t("explorer.entityMap.noAssociations")}</p>}
                <ul className="space-y-1">
                  {neighbours
                    .sort((a, b) => a.node.label.localeCompare(b.node.label))
                    .map((n) => (
                      <li key={n.node.id}>
                        <button type="button" onClick={() => pick(n.node.id)} className="flex w-full flex-col items-start rounded-md border border-card-border px-2.5 py-1.5 text-left hover:bg-row-hover">
                          <span className="flex items-center gap-1.5 text-xs font-semibold">
                            <span className="h-2 w-2 rounded-full" style={{ background: groupColor(n.node.group) }} /> {n.node.label}
                          </span>
                          <span className="font-mono text-[10px] text-muted-foreground">
                            {n.via.map((e) => (e.from === focus ? `${e.field}${e.kind === "to-many" ? " []" : ""}` : `← ${e.from}.${e.field}`)).join(" · ")}
                          </span>
                        </button>
                      </li>
                    ))}
                </ul>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">{t("explorer.entityMap.pickPrompt")}</p>
            )}
          </div>
        </aside>
      </div>
    </Blade>
  )
}

/** A node's field count once its schema is known, else the invitation to load it */
function fieldCountLabel(fieldCount: number | null, t: Translator["t"], n: Translator["n"]): string {
  if (fieldCount === null) return t("explorer.entityMap.clickToLoad")
  return t((fieldCount === 1 ? "explorer.entityMap.fieldsOne" : "explorer.entityMap.fieldsOther"), { count: n(fieldCount) })
}

/** Progress pill over the canvas while schemas load or the layout settles */
function MapProgress({ layingOut, resolving, entities }: { layingOut: boolean; resolving: number; entities: number }) {
  const { t, n } = useTranslation()
  const label = layingOut
    ? t("explorer.entityMap.layingOut", { count: n(entities) })
    : resolving > 0
      ? t("explorer.entityMap.resolvingCount", { count: n(resolving) })
      : null
  if (!label) return null
  return (
    <div className="pointer-events-none absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-2 rounded-pill bg-[#0b1735]/85 px-3 py-1 text-[11px] text-[#e7ebf5] shadow-menu" role="status">
      <Loader2 className="h-3 w-3 animate-spin" /> {label}
    </div>
  )
}

/** How long to wait for further schema arrivals before re-running the layout */
const LAYOUT_SETTLE_MS = 150

/**
 * Lays the graph out off the render path: the spinner paints first, then the force
 * simulation runs once the burst of arriving schemas has settled, seeded from the last
 * positions so nodes drift rather than jump.
 */
function useDebouncedLayout(graph: EntityGraph): { layout: Layout; layingOut: boolean } {
  const [layout, setLayout] = useState<Layout>(new Map())
  const [layingOut, setLayingOut] = useState(true)
  const previous = useRef<Layout>(new Map())

  useEffect(() => {
    if (graph.nodes.length === 0) return
    setLayingOut(true)
    const handle = window.setTimeout(() => {
      const next = layoutEntityGraph(graph, 7, previous.current)
      previous.current = next
      setLayout(next)
      setLayingOut(false)
    }, LAYOUT_SETTLE_MS)
    return () => window.clearTimeout(handle)
  }, [graph])

  return { layout, layingOut }
}
